import { Provider as JotaiProvider } from "jotai";
import { ThemeProvider } from "next-themes";
import { Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { SWRConfig } from "swr";
import { AuthProvider } from "@/auth/auth-provider";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AppShellSkeleton } from "@/layouts/app-shell-skeleton";
import { AuthLayout } from "@/layouts/auth-layout";
import { HomeRedirect, ProjectLayout } from "@/layouts/project-layout";
import { ProtectedLayout } from "@/layouts/protected-layout";
import { pages } from "@/routes/pages";
import { ProtectedRoute } from "@/routes/protected-route";
import { PublicRoute } from "@/routes/public-route";

export function App() {
	return (
		<ThemeProvider
			attribute="class"
			storageKey="gridcast:theme"
			defaultTheme="system"
			enableSystem
			disableTransitionOnChange
			// Pre-paint theming lives in index.html; a data-block type keeps React 19 from warning.
			scriptProps={{ type: "application/json" }}
		>
			<JotaiProvider>
				<SWRConfig value={{ revalidateOnFocus: false, shouldRetryOnError: false }}>
					<BrowserRouter>
						<AuthProvider>
							<TooltipProvider>
								<Suspense fallback={<AppShellSkeleton label="Loading" />}>
									<Routes>
										<Route element={<PublicRoute />}>
											<Route element={<AuthLayout />}>
												<Route path="/login" element={<pages.login />} />
												<Route path="/signup" element={<pages.signup />} />
											</Route>
										</Route>
										<Route element={<AuthLayout />}>
											<Route path="/invite/:token" element={<pages.invite />} />
										</Route>
										<Route element={<ProtectedRoute />}>
											<Route element={<ProtectedLayout />}>
												<Route index element={<HomeRedirect />} />
												<Route path="projects" element={<pages.projects />} />
												<Route path="assistant" element={<pages.assistant />} />
												<Route path="p/:projectId" element={<ProjectLayout />}>
													<Route index element={<Navigate to="overview" replace />} />
													<Route path="overview" element={<pages.overview />} />
													<Route path="content" element={<pages.content />} />
													<Route path="content/:postId" element={<pages.contentDetail />} />
													<Route path="inbox" element={<pages.inbox />} />
													<Route path="analytics" element={<pages.analytics />} />
													<Route path="strategy" element={<pages.strategy />} />
													<Route path="plan" element={<pages.plan />} />
													<Route path="calendar" element={<pages.calendar />} />
													<Route path="competitors" element={<pages.competitors />} />
													<Route path="ads" element={<pages.ads />} />
													<Route path="agents" element={<pages.agents />} />
													<Route path="agents/:agentId" element={<pages.agentDetail />} />
													<Route path="flows" element={<pages.flows />} />
													<Route path="flows/:flowId" element={<pages.flowCanvas />} />
													<Route path="artifacts" element={<pages.artifacts />} />
													<Route path="assistant" element={<pages.assistant />} />
													<Route path="settings" element={<pages.projectSettings />} />
													<Route path="settings/:tab" element={<pages.projectSettings />} />
												</Route>
												<Route path="settings" element={<pages.accountSettings />} />
												<Route path="settings/workspace" element={<pages.workspaceSettings />} />
												<Route path="settings/members" element={<pages.members />} />
												<Route path="settings/roles" element={<pages.roles />} />
												<Route path="settings/billing" element={<pages.billing />} />
											</Route>
										</Route>
										<Route path="*" element={<pages.notFound />} />
									</Routes>
								</Suspense>
							</TooltipProvider>
							<Toaster />
						</AuthProvider>
					</BrowserRouter>
				</SWRConfig>
			</JotaiProvider>
		</ThemeProvider>
	);
}
